import React, { lazy, ReactNode, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  DialogProps,
  Typography,
  Button,
  Box,
  TextField,
  Paper,
  PaperProps,
  useMediaQuery,
  useTheme,
  Grid,
  Tooltip
} from '@mui/material';
import IconButton from 'components/@extended/IconButton';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import DeleteForeverIcon from '@mui/icons-material/DeleteForeverOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EditIcon from '@mui/icons-material/Edit';
import Draggable from 'react-draggable';
import LoadingButton from 'components/@extended/LoadingButton';
import { Divider } from '@mui/material';
import CustomInfoIcon from 'assets/icons/svg/info-icon.svg?react';
import WarningIcon from 'assets/icons/svg/warning-icon.svg?react';
import { Alert } from '@mui/material';
import WarningValidationIcon from 'assets/icons/svg/warning-validation.svg?react';
import CrossIcon from 'assets/icons/svg/cross.svg?react';
import Loadable from 'components/Loadable';
const TableB = Loadable(lazy(() => import('components/tables/TableB')));

const taleHeader = [
  {
    Header: () => <Typography textAlign="center">Related Data</Typography>,
    accessor: 'lossOfAccess',
    disableSortBy: true,
    Cell: ({ value }: { value: any }) => <Typography>{value}</Typography>
  },
  {
    Header: () => <Typography textAlign="center">Count</Typography>,
    accessor: 'count',
    disableSortBy: true,
    Cell: ({ value }: { value: any }) => <Typography textAlign="center">{value}</Typography>
  }
];

interface TableDataType {
  lossOfAccess: string;
  count: number | string;
}
interface ConfirmationDialogProps extends DialogProps {
  open: boolean;
  onClose: () => void;
  message?: string;
  actions?: React.ReactNode;
  customMaxWidth?: string;
  customMinWidth?: string;
  showWarningIcon?: boolean;
  additionalContent?: React.ReactNode;
  onConfirm?: () => void;
  requireConfirmation?: boolean;
  actionType?:
    | 'delete'
    | 'edit'
    | 'unlock-user'
    | 'process-delete'
    | 'access-delete'
    | 'lock-user'
    | 'unsavedChanges'
    | 'logout'
    | 'dpc-delete'
    | 'tagging-technique-delete'
    | 'tagging-field-delete'
    | 'master-data-delete'
    | 'storage-change'
    | 'export-delete'
    | 'storage-delete';
  isLoading?: boolean;
  hideCloseIcon?: boolean;
  confirmButtonLabel?: string;
  cancelButtonLabel?: string;
  showOnlyOkButton?: boolean;
  showYesNoCancelButtons?: boolean;
  onNo?: () => void;
  icon?: React.ReactNode;
  confirmationKey?: any;
  confirmationLabel?: string;
  headingOne?: string;
  headingTwo?: string | ReactNode;
  headingThree?: string | ReactNode;
  tableData?: TableDataType[];
  placeHolder?: string;
  showImpactAreas?: boolean;
  modeltype?: any;
  validationErrorMessage?: string;
  type?: string;
  alertMessage?: string;
  showBreadCrumbs?: any;
  itemTitle1?: any;

  itemTitle2?: any;
  itemTitle3?: any;
  itemTitle4?: any;
  technique?: any;
  techniqueValidation?: any;
  techniqueType?: any;
  titleIconType?: 'info' | 'warning';
  impactAreaTableWidth?: string;
}

const translate = (key: string, options?: any) => (window as any)?.translate?.(key, options);

const BootstrapDialogTitle: React.FC<{ onClose: () => void; hideCloseIcon?: boolean }> = ({ hideCloseIcon, onClose, ...other }) => (
  <DialogTitle sx={{ m: 0, p: 2 }} {...other} style={{ cursor: 'move', textAlign: 'center' }} id="draggable-dialog-title">
    {!hideCloseIcon && (
      <IconButton
        aria-label="close"
        onClick={onClose}
        color="secondary"
        className="CD-Close"
        sx={{
          position: 'absolute',
          right: 16,
          top: 16
        }}
      >
        <CloseOutlinedIcon />
      </IconButton>
    )}
  </DialogTitle>
);

const PaperComponent = React.forwardRef((props: PaperProps, ref: React.Ref<HTMLDivElement>) => (
  <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
    <Paper ref={ref} {...props} />
  </Draggable>
));

const getActionDetails = (
  actionType?: string,
  confirmationKey?: string,
  headingOne?: string,
  headingTwo?: string,
  tableData?: TableDataType[],
  headingThree?: string,
  showImpactAreas?: boolean,
  modeltype?: any,
  itemTitle1?: any,
  type?: any,
  itemTitle2?: any,
  itemTitle3?: any,
  itemTitle4?: any,
  technique?: any,
  techniqueValidation?: any,
  techniqueType?: any,
  TitleIcon: any,
  titleIconType: any,
  impactAreaTableWidth: string
) => {
  switch (actionType) {
    case 'process-delete':
      return {
        icon: <DeleteForeverIcon color="error" sx={{ fontSize: 50, mb: 2 }} />,
        message: translate('components.confirmationDialog.processDelete.message'),
        additionalContent: (
          <Typography variant="body2" color="textSecondary">
            {translate('components.confirmationDialog.processDelete.additionalContent')}{' '}
          </Typography>
        ),
        confirmationLabel: translate('components.confirmationDialog.processDelete.confirmationLabel')
      };
    case 'delete':
      return {
        icon: <DeleteForeverIcon color="error" sx={{ fontSize: 50, mb: 2 }} />,
        message: translate('components.confirmationDialog.delete.message'),
        additionalContent: (
          <Typography variant="body2" color="textSecondary">
            {translate('components.confirmationDialog.delete.additionalContent')}{' '}
          </Typography>
        ),
        confirmationLabel: translate('components.confirmationDialog.delete.confirmationLabel')
      };
    case 'edit':
      return {
        icon: <EditIcon color="primary" sx={{ fontSize: 50, mb: 2 }} />,
        message: translate('components.confirmationDialog.edit.message'),
        additionalContent: (
          <Typography variant="body2" color="textSecondary">
            {translate('components.confirmationDialog.edit.additionalContent')}{' '}
          </Typography>
        ),
        confirmationLabel: translate('components.confirmationDialog.edit.confirmationLabel')
      };

    case 'unlock-user':
      return {
        icon: titleIconType ? (
          <TitleIcon style={{ transform: 'translateX(-75%)' }} />
        ) : (
          <WarningAmberOutlinedIcon color="error" sx={{ fontSize: 50, mb: 2 }} />
        ),
        confirmationLabel: translate('components.confirmationDialog.edit.confirmationLabel')
      };
    case 'dpc-delete':
      return {
        icon: (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box>
                <Box>
                  <Typography variant="h5" fontWeight="bold" textAlign={'left'} marginLeft={1}>
                    {translate('components.confirmationDialog.deleteField')}
                  </Typography>
                </Box>
                <Box>
                  <Box>
                    {/* <BreadcrumbComponent
                      itemTitle1={'DESIGN'}
                      itemTitle2={'DOCUMENT LIST'}
                      itemTitle3={'DOCUMENT TYPE'}
                      itemTitle4={'DATA POINT CONFIGURATION'}
                    /> */}
                  </Box>
                </Box>
              </Box>
              <WarningAmberOutlinedIcon color="error" sx={{ fontSize: 80 }} />
            </Box>
            <Divider style={{ width: '100%', marginBottom: '10px' }} />
          </>
        )
      };
    case 'master-data-delete':
      return {
        icon: (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box>
                <Box>
                  <Typography variant="h5" fontWeight="bold" textAlign={'left'} marginLeft={1}>
                    {translate('components.confirmationDialog.deleteField')}
                  </Typography>
                </Box>
                <Box>
                  <Box>
                    {/* <BreadcrumbComponent
                      itemTitle1={'DESIGN'}
                      itemTitle2={'MASTER DATA'}
                      itemTitle3={type === 'processMaster' ? 'PROCESS MASTER' : 'FIELD MASTER'}
                    /> */}
                  </Box>
                </Box>
              </Box>
              <WarningAmberOutlinedIcon color="error" sx={{ fontSize: 80 }} />
            </Box>
            <Divider style={{ width: '100%', marginBottom: '10px' }} />
          </>
        ),
        message: headingOne,
        additionalContent: (
          <>
            <Typography variant="body2" color="textSecondary">
              {headingTwo}{' '}
            </Typography>
            {showImpactAreas && (
              <>
                <br />
                <Typography variant="h2">{translate('components.confirmationDialog.impactAreas')}</Typography>
                <Box
                  sx={{
                    maxWidth: '350px',
                    margin: '0 auto'
                  }}
                >
                  {' '}
                  <TableB
                    columns={taleHeader}
                    data={tableData!}
                    createButton={false}
                    showGlobalFilter={false}
                    showPagination={false}
                    maxHeight={200}
                    className=" table dashboard-table text-wrap ConfirmationDialogTable"
                  />
                </Box>
              </>
            )}
          </>
        ),
        confirmationLabel: headingThree
      };
    case 'tagging-technique-delete':
      return {
        icon: (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box>
                <Box>
                  <Typography variant="h5" fontWeight="bold" textAlign={'left'} marginLeft={1}>
                    {translate('components.confirmationDialog.deleteTechnique')}
                  </Typography>
                </Box>
                <Box>
                  <Box>
                    {/* <BreadcrumbComponent
                      itemTitle1={'DESIGN'}
                      itemTitle2={'DOCUMENT LIST'}
                      itemTitle3={'DOCUMENT TYPE'}
                      itemTitle4={'CONFIGURATION & TAGGING'}
                    /> */}
                  </Box>
                </Box>
              </Box>
              <WarningAmberOutlinedIcon color="error" sx={{ fontSize: 80 }} />
            </Box>
            <Divider style={{ width: '100%', marginBottom: '10px' }} />
          </>
        ),

        message: headingOne,
        additionalContent: (
          <>
            <Box mb={2}>
              <Typography mb={1} variant="body2" color="textSecondary">
                {headingOne}
              </Typography>
              <Grid container rowSpacing={0.3} columnSpacing={3} justifyContent="center">
                <Grid item>
                  <Typography variant="body2" color="textSecondary">
                    {translate('components.confirmationDialog.technique')} : {technique || '-'}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body2" color="textSecondary">
                    {translate('components.confirmationDialog.type')} : {techniqueType || '-'}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body2" color="textSecondary">
                    {translate('components.confirmationDialog.validation')} : {techniqueValidation || '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            <Box mt={3} mb={4}>
              <Typography mb="5px" variant="h2">
                {translate('components.confirmationDialog.impactAreas')}
              </Typography>
              <Box
                sx={{
                  maxWidth: impactAreaTableWidth,
                  margin: '0 auto'
                }}
              >
                <TableB
                  columns={taleHeader}
                  data={tableData!}
                  createButton={false}
                  showGlobalFilter={false}
                  showPagination={false}
                  maxHeight={200}
                  className=" table dashboard-table text-wrap ConfirmationDialogTable"
                  hideHeader
                />
              </Box>
            </Box>
            <Typography className="confrimation-label" color="textSecondary">
              {headingTwo}
            </Typography>
          </>
        ),
        confirmationLabel: headingThree
      };

    case 'tagging-field-delete':
      return {
        icon: (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box>
                <Box>
                  <Typography variant="h5" fontWeight="bold" textAlign={'left'} marginLeft={1}>
                    {translate('components.confirmationDialog.deleteField')}
                  </Typography>
                </Box>
                <Box>
                  <Box>
                    {/* <BreadcrumbComponent
                      itemTitle1={'DESIGN'}
                      itemTitle2={'DOCUMENT LIST'}
                      itemTitle3={'DOCUMENT TYPE'}
                      itemTitle4={'CONFIGURATION & TAGGING'}
                    /> */}
                  </Box>
                </Box>
              </Box>
              <WarningAmberOutlinedIcon color="error" sx={{ fontSize: 80 }} />
            </Box>
            <Divider style={{ width: '100%', marginBottom: '10px' }} />
          </>
        ),

        message: headingOne,
        additionalContent: (
          <>
            <Box>
              <Typography variant="body2" color="textSecondary">
                {headingTwo}
              </Typography>
              <Box mt={3} mb={1}>
                <Typography mb="5px" variant="h2">
                  {translate('components.confirmationDialog.impactAreas')}
                </Typography>
                <Box
                  sx={{
                    maxWidth: impactAreaTableWidth,
                    margin: '0 auto'
                  }}
                >
                  <TableB
                    columns={taleHeader}
                    data={tableData!}
                    createButton={false}
                    showGlobalFilter={false}
                    showPagination={false}
                    maxHeight={200}
                    className=" table dashboard-table text-wrap ConfirmationDialogTable"
                    hideHeader
                  />
                </Box>
              </Box>
            </Box>
          </>
        ),
        confirmationLabel: headingThree
      };

    case 'storage-change':
      return {
        icon: titleIconType ? (
          <TitleIcon style={{ transform: 'translateX(-75%)' }} />
        ) : (
          <WarningAmberOutlinedIcon color="error" sx={{ fontSize: 80 }} />
        ),

        message: headingOne,
        additionalContent: (
          <>
            <Typography variant="h5">{headingTwo}</Typography>

            {showImpactAreas && (
              <>
                <br />
                <Typography variant="h2">{translate('components.confirmationDialog.impactAreas')}</Typography>
                <Box
                  sx={{
                    maxWidth: '350px',
                    margin: '0 auto'
                  }}
                >
                  {' '}
                  <TableB
                    columns={taleHeader}
                    data={tableData!}
                    createButton={false}
                    showGlobalFilter={false}
                    showPagination={false}
                    maxHeight={200}
                    className=" table dashboard-table text-wrap ConfirmationDialogTable"
                  />
                </Box>
              </>
            )}
          </>
        ),
        confirmationLabel: headingThree
      };
    case 'access-delete':
      return {
        icon:
          modeltype === 'documentDelete' ? (
            <>
              {' '}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box>
                  <Box>
                    <Typography variant="h5" fontWeight="bold" textAlign={'left'} marginLeft={1}>
                      {translate('components.confirmationDialog.deleteDocumentType')}
                    </Typography>
                  </Box>
                  <Box>
                    <Box>{/* <BreadcrumbComponent itemTitle1={itemTitle1} itemTitle2={itemTitle2} itemTitle3={itemTitle3} /> */}</Box>
                  </Box>
                </Box>
                <WarningAmberOutlinedIcon color="error" sx={{ fontSize: 80 }} />
              </Box>
              <Divider style={{ width: '100%', marginBottom: '10px' }} />
            </>
          ) : (
            <>
              {titleIconType ? (
                <TitleIcon style={{ transform: 'translateX(-75%)' }} />
              ) : (
                <WarningAmberIcon color="error" sx={{ fontSize: 50, mb: 2 }} />
              )}
            </>
          ),
        message: headingOne,
        additionalContent: (
          <>
            <Typography color="textSecondary">{headingTwo}</Typography>
            {showImpactAreas && (
              <>
                <Typography variant="h2" mb="5px" mt="20px">
                  {translate('components.confirmationDialog.impactAreas')}
                </Typography>
                <Box
                  sx={{
                    maxWidth: impactAreaTableWidth,
                    margin: '0 auto'
                  }}
                >
                  <TableB
                    columns={taleHeader}
                    data={tableData!}
                    createButton={false}
                    showGlobalFilter={false}
                    showPagination={false}
                    maxHeight={200}
                    className=" table dashboard-table text-wrap ConfirmationDialogTable"
                    hideHeader
                  />
                </Box>
              </>
            )}
          </>
        ),
        confirmationLabel: headingThree
      };
    case 'export-delete':
      return {
        icon:
          modeltype === 'export-delete' ? (
            <>
              {' '}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box>
                  <Box>
                    <Typography variant="h5" fontWeight="bold" textAlign={'left'} marginLeft={1}>
                      {translate('components.confirmationDialog.deleteField')}
                    </Typography>
                  </Box>
                  <Box>
                    <Box>{/* <BreadcrumbComponent itemTitle1={itemTitle1} itemTitle2={itemTitle2} itemTitle3={itemTitle3} /> */}</Box>
                  </Box>
                </Box>
                <WarningAmberOutlinedIcon color="error" sx={{ fontSize: 80 }} />
              </Box>
              <Divider style={{ width: '100%', marginBottom: '10px' }} />
            </>
          ) : (
            <WarningAmberIcon color="error" sx={{ fontSize: 50, mb: 2 }} />
          ),
        message: headingOne,
        additionalContent: (
          <>
            <Typography variant="body2" color="textSecondary">
              {headingTwo}
            </Typography>

            {showImpactAreas && (
              <>
                <br />
                <Typography variant="h2">{translate('components.confirmationDialog.impactAreas')}</Typography>
                <Box
                  sx={{
                    maxWidth: '350px',
                    margin: '0 auto'
                  }}
                >
                  {' '}
                  <TableB
                    columns={taleHeader}
                    data={tableData!}
                    createButton={false}
                    showGlobalFilter={false}
                    showPagination={false}
                    maxHeight={200}
                    className=" table dashboard-table text-wrap ConfirmationDialogTable"
                  />
                </Box>
              </>
            )}
          </>
        ),
        confirmationLabel: headingThree
      };
    case 'storage-delete':
      return {
        icon:
          modeltype === 'storage' ? (
            <>
              {' '}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box>
                  <Box>
                    <Typography variant="h5" fontWeight="bold" textAlign={'left'} marginLeft={1}>
                      {translate('components.confirmationDialog.deleteStorage')}
                    </Typography>
                  </Box>
                  <Box>
                    <Box>{/* <BreadcrumbComponent itemTitle1={itemTitle1} itemTitle2={itemTitle2} itemTitle3={itemTitle3} /> */}</Box>
                  </Box>
                </Box>
                <WarningAmberOutlinedIcon color="error" sx={{ fontSize: 80 }} />
              </Box>
              <Divider style={{ width: '100%', marginBottom: '10px' }} />
            </>
          ) : (
            <WarningAmberIcon color="error" sx={{ fontSize: 50, mb: 2 }} />
          ),
        message: headingOne,
        additionalContent: (
          <>
            <Typography variant="body2" color="textSecondary">
              {headingTwo}
            </Typography>

            {showImpactAreas && (
              <>
                <br />
                <Typography variant="h2">{translate('components.confirmationDialog.impactAreas')}</Typography>
                <Box
                  sx={{
                    maxWidth: '350px',
                    margin: '0 auto'
                  }}
                >
                  {' '}
                  <TableB
                    columns={taleHeader}
                    data={tableData!}
                    createButton={false}
                    showGlobalFilter={false}
                    showPagination={false}
                    maxHeight={200}
                    className=" table dashboard-table text-wrap ConfirmationDialogTable"
                  />
                </Box>
              </>
            )}
          </>
        ),
        confirmationLabel: headingThree
      };

    case 'lock-user':
      return {
        icon: <WarningAmberIcon color="error" sx={{ fontSize: 50, mb: 2 }} />,
        message: headingOne,
        confirmationLabel: headingThree
      };

    default:
      return {
        icon: <WarningAmberOutlinedIcon color="warning" sx={{ fontSize: 50, mb: 2 }} />,
        message: translate('components.confirmationDialog.default.message'),
        additionalContent: null,
        confirmationLabel: translate('components.confirmationDialog.default.confirmationLabel')
      };
  }
};

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  message,
  actions,
  customMaxWidth,
  customMinWidth = '400px',
  maxWidth = 'xs',
  showWarningIcon = true,
  additionalContent,
  onConfirm,
  requireConfirmation = false,
  showBreadCrumbs = false,
  actionType,
  isLoading,
  hideCloseIcon = true,
  confirmButtonLabel,
  cancelButtonLabel,
  showOnlyOkButton,
  showYesNoCancelButtons,
  icon: headerIcon,
  confirmationKey,
  confirmationLabel,
  headingOne = '',
  headingTwo = '',
  headingThree = '',
  tableData = [],
  showImpactAreas = false,
  modeltype = '',
  itemTitle1 = '',

  itemTitle2 = '',
  itemTitle3 = '',
  itemTitle4 = '',
  placeHolder = 'Type the text',
  validationErrorMessage = '',
  type = '',
  alertMessage = '',
  technique = '',
  techniqueValidation = '',
  techniqueType = '',
  titleIconType,
  impactAreaTableWidth = '250px',
  ...props
}) => {
  const [confirmationCheck, setconfirmationCheck] = useState('');
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleConfirm = () => {
    if (requireConfirmation && confirmationKey?.trim() && confirmationCheck?.trim() !== confirmationKey?.trim()) {
      setError(validationErrorMessage);
    } else {
      setError('');
      if (onConfirm) onConfirm();
      setconfirmationCheck('');
    }
  };

  const handleClose = () => {
    setError('');
    setconfirmationCheck('');
    onClose();
  };

  const defaultActions = (
    <>
      {showOnlyOkButton ? (
        <Box display="flex" justifyContent="flex-end" gap={3}>
          <LoadingButton
            variant="contained"
            className="buttonPrimary"
            onClick={props.onNo}
            loading={isLoading}
            style={{ minWidth: '120px', marginRight: '30px' }}
          >
            {confirmButtonLabel ?? 'OK'}
          </LoadingButton>
        </Box>
      ) : showYesNoCancelButtons ? (
        <>
          <Box display="flex" justifyContent="flex-end" gap={3}>
            <Tooltip title={translate('pages.design.document.tooltips.cancelButton')} placement="top" arrow>
              <Button
                variant="contained"
                className="buttonSecondary"
                onClick={handleClose}
                style={{ minWidth: '120px', marginLeft: '30px' }}
              >
                {cancelButtonLabel ?? 'Cancel'}
              </Button>
            </Tooltip>
            <Tooltip title={translate('pages.design.document.tooltips.noButton')} placement="top" arrow>
              <Button variant="contained" className="buttonSecondary" onClick={props.onNo} style={{ minWidth: '120px' }}>
                No
              </Button>
            </Tooltip>
            <Tooltip title={translate('pages.design.document.tooltips.yesButton')} placement="top" arrow>
              <LoadingButton
                variant="contained"
                className="buttonPrimary"
                onClick={handleConfirm}
                loading={isLoading}
                style={{ minWidth: '120px', marginRight: '30px' }}
              >
                {confirmButtonLabel ?? 'Yes'}
              </LoadingButton>
            </Tooltip>
          </Box>
        </>
      ) : type === 'clientstoragedelete' ? (
        <>
          <Button variant="contained" className="buttonSecondary" onClick={handleClose} style={{ minWidth: '120px' }}>
            {cancelButtonLabel ?? 'Cancel'}
          </Button>
          <LoadingButton
            variant="contained"
            className="buttonPrimary"
            onClick={handleConfirm}
            loading={isLoading}
            style={{ minWidth: '120px' }}
          >
            {confirmButtonLabel ?? 'Delete'}
          </LoadingButton>
        </>
      ) : (
        <>
          <Button variant="contained" className="buttonSecondary" color="secondary" onClick={handleClose} style={{ minWidth: '120px' }}>
            {cancelButtonLabel ?? 'Cancel'}
          </Button>
          <LoadingButton
            variant="contained"
            className="buttonPrimary"
            onClick={handleConfirm}
            loading={isLoading}
            style={{ minWidth: '120px' }}
          >
            {confirmButtonLabel ?? 'Confirm'}
          </LoadingButton>
        </>
      )}
    </>
  );

  const TitleIcon = titleIconType === 'info' ? CustomInfoIcon : WarningIcon;

  const {
    icon,
    message: defaultMessage,
    additionalContent: defaultAdditionalContent,
    confirmationLabel: defaultConfirmationLabel
  } = getActionDetails(
    actionType,
    confirmationKey ?? '',
    headingOne ?? '',
    headingTwo ?? '',
    tableData ?? [],
    headingThree ?? '',
    showImpactAreas ?? false,
    modeltype ?? '',
    itemTitle1 ?? '',
    type ?? '',
    itemTitle2 ?? '',
    itemTitle3 ?? '',
    itemTitle4 ?? '',
    technique ?? '',
    techniqueValidation ?? '',
    techniqueType ?? '',
    TitleIcon,
    titleIconType,
    impactAreaTableWidth
  );

  return (
    <Dialog
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
      PaperComponent={PaperComponent}
      open={open}
      sx={{
        ...props.sx,
        minWidth: !isMobile ? customMinWidth : '100%',
        ...(customMaxWidth && {
          maxWidth: customMaxWidth,
          marginLeft: 'auto',
          marginRight: 'auto'
        })
      }}
      className="ConfirmationDialog"
      PaperProps={{
        sx: {
          backgroundImage: 'linear-gradient(rgb(142 142 142 / 16%), rgba(255, 255, 255, 0.16))',
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.background.default
        }
      }}
    >
      <Box
        sx={{
          minWidth: !isMobile ? customMinWidth : '100%'
        }}
      >
        <Box className="CD-Box">
          {/* <BootstrapDialogTitle onClose={handleClose} hideCloseIcon={hideCloseIcon} /> */}
          <DialogContent sx={{ p: 3, textAlign: 'center' }} className={`CD-Content ${actionType === 'logout' ? 'logout-margin' : ''}`}>
            {type === 'unsavedChanges' ? (
              <Grid columnSpacing={2} container alignContent={'center'} justifyContent="center" flexWrap="nowrap">
                <Grid item xs={10}>
                  <Typography className="popup-description" gutterBottom mb={2}>
                    {alertMessage || defaultMessage}
                  </Typography>
                  <Typography className="popup-description" gutterBottom>
                    {message || defaultMessage}
                  </Typography>
                </Grid>
                <Grid item width={0}>
                  <WarningIcon />
                </Grid>
              </Grid>
            ) : (
              <>
                <Grid container alignItems="center" wrap="nowrap" mt="5px">
                  <Grid item flex={1}>
                    <Typography my={0} variant="h5" gutterBottom className="confirmation-popup-title">
                      {message || defaultMessage}
                    </Typography>
                  </Grid>
                  <Grid width={0}>
                    {titleIconType ? <TitleIcon style={{ transform: 'translateX(-75%)' }} /> : showWarningIcon && (headerIcon || icon)}
                  </Grid>
                </Grid>
                <Divider className="popup-tittle-divider" sx={{ my: 1 }} />
              </>
            )}

            {additionalContent || defaultAdditionalContent}
            {requireConfirmation && (
              <Grid container justifyContent="center">
                <Grid item lg={12}>
                  <Typography className="mt-20 confrimation-label">
                    {confirmationLabel || defaultConfirmationLabel || translate('components.confirmationDialog.defaultConfirmationLabel')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={7}>
                  <TextField
                    fullWidth
                    value={confirmationCheck}
                    onChange={(e) => setconfirmationCheck(e.target.value)}
                    sx={{ mt: 1 }}
                    error={!!error}
                    placeholder={placeHolder}
                    inputProps={{
                      ...(type === 'DocumentList' ? { maxLength: 50 } : {}),
                      sx: { backgroundColor: theme.palette.background.paper }
                    }}
                  />
                </Grid>

                {error && (
                  <Grid xs={12} mt={1}>
                    <Alert
                      className="popup-error-message-alert-container"
                      severity="error"
                      icon={<WarningValidationIcon />}
                      action={<CrossIcon onClick={() => setError('')} className="cursor-pointer" />}
                    >
                      <Typography color="error" className="confrimation-label error-message">
                        {error}
                      </Typography>
                    </Alert>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions className="CD-Actions" sx={{ backgroundColor: theme.palette.background.paper }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              sx={{ width: 1, gap: actionType === 'logout' ? (isMobile ? 2 : 16) : isMobile ? 2 : 10 }}
            >
              {actions || defaultActions}
            </Stack>
          </DialogActions>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ConfirmationDialog;
